import { useViewjobOneGraphModel, useViewjobSourceUrl } from "./ViewJobDataContext";
import { getCachedViewJobHint } from "../../../shared/native-modules/tare/getCachedViewJobHint";

import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";

export type OutreachSource = ValidOutreachSource | null;
export type OutreachType = ValidOutreachType | null;

export interface EmployerOutreachData {
  isFromEmployerOutreach: boolean;
  outreachSource: OutreachSource;
  outreachType: OutreachType;
  matchFactor: string | null;
  rezContactSource?: string;
  from: string | null;
}

// `from` values that activate outreach mode (Homepage + MyJobs invitations).
export const EMPLOYER_OUTREACH_FROM_VALUES: ReadonlySet<string> = new Set([
  "hp.employerOutreach",
  "myjobs.employerOutreach",
]);

export const DEFAULT_REZ_CONTACT_SOURCE = "HOMEPAGE";

const REZ_CONTACT_SOURCE_BY_FROM: Readonly<Record<string, string>> = {
  "myjobs.employerOutreach": "rn_myjobs_invitations",
};

export const resolveRezContactSource = (fromParam?: string | null): string =>
  REZ_CONTACT_SOURCE_BY_FROM[fromParam ?? ""] ?? DEFAULT_REZ_CONTACT_SOURCE;

const VALID_OUTREACH_SOURCES = [
  "AUTOSOURCER",
  "SMART_SOURCING",
  "PREMIUM_MATCH_CANDIDATES",
] as const;
const VALID_OUTREACH_TYPES = [
  "FAST_APPLY_WITHOUT_INTERVIEW",
  "FULL_APPLY_WITHOUT_INTERVIEW",
  "FAST_APPLY_WITH_INTERVIEW",
  "FULL_APPLY_WITH_INTERVIEW",
] as const;

type ValidOutreachSource = (typeof VALID_OUTREACH_SOURCES)[number];
type ValidOutreachType = (typeof VALID_OUTREACH_TYPES)[number];
const VALID_OUTREACH_SOURCES_SET: ReadonlySet<string> = new Set(VALID_OUTREACH_SOURCES);
const VALID_OUTREACH_TYPES_SET: ReadonlySet<string> = new Set(VALID_OUTREACH_TYPES);

const toValidOutreachSource = (value?: string): ValidOutreachSource | null => {
  if (!value) return null;
  const upper = value.toUpperCase();
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  return VALID_OUTREACH_SOURCES_SET.has(upper) ? (upper as ValidOutreachSource) : null;
};

const toValidOutreachType = (value?: string): ValidOutreachType | null => {
  if (!value) return null;
  const upper = value.toUpperCase();
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- RN-5783 baseline: pre-existing cast; migrate to zod/proper types (see docs/type-assertions.md)
  return VALID_OUTREACH_TYPES_SET.has(upper) ? (upper as ValidOutreachType) : null;
};

export const DEFAULT_EMPLOYER_OUTREACH_DATA: EmployerOutreachData = {
  isFromEmployerOutreach: false,
  outreachSource: null,
  outreachType: null,
  matchFactor: null,
  rezContactSource: DEFAULT_REZ_CONTACT_SOURCE,
  from: null,
};

export interface EmployerOutreachContextValue {
  data: EmployerOutreachData;
  isConfirmed: boolean;
  markAsConfirmed: () => void;
}

// Exported for use in mock providers (storybooks/tests)
export const EmployerOutreachContext = createContext<EmployerOutreachContextValue | undefined>(
  undefined,
);

export const EmployerOutreachProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.JSX.Element => {
  const sourceUrl = useViewjobSourceUrl();
  const ogModel = useViewjobOneGraphModel();
  const jobKey = ogModel?.job?.jobKey ?? null;

  const [data, setData] = useState<EmployerOutreachData>(DEFAULT_EMPLOYER_OUTREACH_DATA);
  const [isConfirmed, setIsConfirmed] = useState<boolean>(false);

  const markAsConfirmed = useCallback(() => {
    setIsConfirmed(true);
  }, []);

  useEffect(() => {
    let isCancelled = false;

    if (!sourceUrl || !jobKey) {
      setData(DEFAULT_EMPLOYER_OUTREACH_DATA);
      return;
    }

    const fetchEmployerOutreachData = async () => {
      try {
        const urlObj = new URL(sourceUrl);
        const fromParam = urlObj.searchParams.get("from");

        const isFromEmployerOutreach = EMPLOYER_OUTREACH_FROM_VALUES.has(fromParam ?? "");

        if (!isFromEmployerOutreach) {
          if (!isCancelled) {
            setData(DEFAULT_EMPLOYER_OUTREACH_DATA);
          }
          return;
        }

        const hint = await getCachedViewJobHint(jobKey);
        const matchFactorFromCache = hint?.matchFactor;

        if (!matchFactorFromCache) {
          if (!isCancelled) {
            setData(DEFAULT_EMPLOYER_OUTREACH_DATA);
          }
          return;
        }

        const [outreachSourceValue, outreachTypeValue] = matchFactorFromCache.split(".");

        const outreachSource: OutreachSource = toValidOutreachSource(outreachSourceValue);
        const outreachType: OutreachType = toValidOutreachType(outreachTypeValue);

        if (!isCancelled) {
          setData({
            isFromEmployerOutreach: true,
            outreachSource,
            outreachType,
            matchFactor: matchFactorFromCache,
            rezContactSource: resolveRezContactSource(fromParam),
            from: fromParam,
          });
        }
      } catch (error) {
        console.error("Error fetching employer outreach source:", error);
        if (!isCancelled) {
          setData(DEFAULT_EMPLOYER_OUTREACH_DATA);
        }
      }
    };

    void fetchEmployerOutreachData();

    return () => {
      isCancelled = true;
    };
  }, [sourceUrl, jobKey]);

  const value = useMemo<EmployerOutreachContextValue>(
    () => ({
      data,
      isConfirmed,
      markAsConfirmed,
    }),
    [data, isConfirmed, markAsConfirmed],
  );

  return (
    <EmployerOutreachContext.Provider value={value}>{children}</EmployerOutreachContext.Provider>
  );
};

export const useEmployerOutreachContext = (): EmployerOutreachContextValue => {
  const context = useContext(EmployerOutreachContext);
  if (!context) {
    throw new Error("useEmployerOutreachContext must be used within an EmployerOutreachProvider");
  }

  return context;
};

/**
 * Hook to access confirm interest functionality
 * Returns undefined if called outside of EmployerOutreachProvider
 * This allows the hook to be used flexibly throughout the component tree
 */
export function useConfirmInterestContext() {
  return useContext(EmployerOutreachContext);
}
